<template>
    <div class="flex flex-col items-start w-full max-w-[960px] mx-auto">
        <!-- Header -->
        <div class="pb-8 flex items-center self-stretch">
            <div>
                <h1 class="text-[#1E293B] dark:text-slate-100 text-2xl font-semibold leading-8">
                    Unfiled redemptions
                </h1>
                <p class="text-[#64748B] dark:text-slate-400 text-sm font-normal leading-5 mt-1">
                    We couldn't match these redemptions to a trip. Please review
                    and move them to the right one.
                </p>
            </div>
        </div>

        <div v-if="loading" class="relative min-h-[200px] w-full">
            <LoadingState :loading="true" />
        </div>

        <div
            v-else-if="redemptions.length === 0"
            class="w-full flex items-center justify-center min-h-[400px]"
        >
            <EmptyState
                icon="checked-suitcase"
                title="All caught up!"
                description="No unfiled redemptions at the moment"
                :hasAction="false"
            />
        </div>

        <!-- Redemptions list -->
        <div v-else class="flex flex-col items-start gap-4 w-full">
            <div
                v-for="redemption in redemptions"
                :key="redemption.id"
                class="flex flex-col sm:flex-row w-full px-5 py-4 items-start sm:items-center gap-4 sm:gap-6 rounded-lg bg-white dark:bg-slate-800 shadow-sm"
            >
                <!-- Left: Icon + Info (flex: 1) -->
                <div class="flex items-center gap-3 flex-1 min-w-0">
                    <!-- Type Icon -->
                    <div
                        class="w-10 h-10 flex shrink-0 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-full items-center justify-center"
                    >
                        <RedemptionIcon
                            :redemption="{
                                ...redemption,
                                date_type: 'start',
                            }"
                        />
                    </div>

                    <!-- Title & Meta -->
                    <div class="min-w-0">
                        <p
                            class="text-sm font-medium text-[#1E293B] dark:text-slate-100 leading-5 truncate"
                        >
                            {{ getRedemptionTitle(redemption) }}
                        </p>
                        <p
                            class="text-sm text-[#64748B] dark:text-slate-400 leading-5 flex items-center gap-2 flex-wrap"
                        >
                            <span v-if="redemption.start_date">
                                {{ formatDateShort(redemption.start_date) }}
                                <span v-if="redemption.end_date">
                                    –
                                    {{ formatDateShort(redemption.end_date) }}
                                </span>
                            </span>
                            <template v-if="getLayoverCount(redemption) > 0">
                                <span
                                    class="w-1 h-1 bg-slate-400 rounded-full inline-block"
                                ></span>
                                <span>
                                    {{ getLayoverCount(redemption) }}
                                    layover{{
                                        getLayoverCount(redemption) > 1
                                            ? 's'
                                            : ''
                                    }}
                                </span>
                            </template>
                            <template v-if="redemption.number_of_travelers">
                                <span
                                    class="w-1 h-1 bg-slate-400 rounded-full inline-block"
                                ></span>
                                <span>
                                    {{ redemption.number_of_travelers }}
                                    traveler{{
                                        redemption.number_of_travelers > 1
                                            ? 's'
                                            : ''
                                    }}
                                </span>
                            </template>
                        </p>
                    </div>
                </div>

                <!-- Right: Actions -->
                <div
                    class="flex items-center gap-3 sm:gap-4 shrink-0 w-full sm:w-auto flex-wrap sm:flex-nowrap"
                >
                    <!-- Warning for needs_details -->
                    <span
                        v-if="redemption.import_status === 'needs_details'"
                        class="text-xs sm:text-sm text-[#EF4444] font-normal leading-5 flex items-center gap-1 w-full sm:w-auto"
                    >
                        <Icon
                            icon="caution"
                            class="text-[#EF4444]"
                            :width="16"
                            :height="16"
                        />
                        Review before adding to trip
                    </span>

                    <!-- Add to trip button -->
                    <Tooltip
                        text="Add redemption to trip"
                        yPlacement="top"
                        xPlacement="center"
                    >
                        <template #trigger>
                            <button
                                class="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium leading-5 text-white bg-[#FD421D] rounded shadow-sm flex-1 sm:flex-none"
                                :class="
                                    redemption.import_status === 'needs_details'
                                        ? '!bg-[#FD421D] !text-white !opacity-50 !cursor-not-allowed hover:!bg-[#FD421D]'
                                        : 'hover:bg-[#e63a18]'
                                "
                                :disabled="
                                    redemption.import_status === 'needs_details'
                                "
                                @click="openAttachModal(redemption)"
                            >
                                <Icon
                                    icon="plus"
                                    class="text-white"
                                    pathClass="stroke-white"
                                    :width="16"
                                    :height="16"
                                />
                                Add to trip
                            </button>
                        </template>
                    </Tooltip>

                    <!-- Review button -->
                    <Tooltip
                        text="Review redemption details"
                        yPlacement="top"
                        xPlacement="center"
                    >
                        <template #trigger>
                            <button
                                class="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium leading-5 text-[#1E293B] dark:text-slate-100 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 flex-1 sm:flex-none"
                                @click="openEditModal(redemption)"
                            >
                                <Icon
                                    icon="pencil"
                                    class="text-slate-500 dark:text-slate-400"
                                    :width="16"
                                    :height="16"
                                />
                                Review
                            </button>
                        </template>
                    </Tooltip>

                    <!-- Delete button -->
                    <Tooltip
                        text="Delete redemption"
                        yPlacement="top"
                        xPlacement="center"
                    >
                        <template #trigger>
                            <button
                                class="flex items-center justify-center p-2 bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded shadow-sm hover:bg-red-50 text-slate-400 hover:text-red-500"
                                @click="openDeleteConfirm(redemption)"
                            >
                                <Icon icon="trash" :width="16" :height="16" />
                            </button>
                        </template>
                    </Tooltip>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div class="w-full pt-5" v-if="totalPages > 1">
            <Pagination
                :current-page="currentPage"
                :total-pages="totalPages"
                :per-page="perPage"
                :total-results="totalResults"
                :max-visible-buttons="5"
                @page-changed="handlePageChange"
            />
        </div>

        <!-- Attach to Trip Modal -->
        <TripSelectorModal
            :isOpen="showAttachModal"
            :redemption="selectedRedemption"
            mode="add"
            @close="closeAttachModal"
            @moved="handleAttachSuccess"
            @new-trip="handleNewTripFromAttach"
        />

        <CreateTripForRedemptionModal
            :isOpen="showCreateTripForRedemptionModal"
            :redemption="createTripRedemption"
            @close="showCreateTripForRedemptionModal = false"
            @created="handleTripCreatedWithRedemption"
        />

        <EditRedemption v-if="showRedemptionEditModal" />

        <!-- Delete Confirmation Modal -->
        <ConfirmationDialog
            :show="showDeleteConfirm"
            :dialog="deleteConfirmMessage"
            @cancel="closeDeleteConfirm"
            @confirm="confirmDeleteRedemption"
            :buttons="[
                {
                    type: 'cancel',
                    text: 'Cancel',
                },
                {
                    type: 'confirm',
                    text: 'Delete redemption',
                    value: true,
                    classes: '!bg-red-500 text-white hover:!bg-red-600',
                },
            ]"
        >
            <template #title>
                <div class="flex items-center gap-3 self-stretch">
                    <div
                        class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                    >
                        <Icon icon="trash" :pathClass="'fill-[#EF4444]'" />
                    </div>
                    <span
                        class="text-[#1E293B] dark:text-slate-100 font-inter text-xl font-medium leading-7"
                    >
                        Delete redemption
                    </span>
                </div>
            </template>
        </ConfirmationDialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import dayjs from 'dayjs';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import TripSelectorModal from '@/components/trips/modals/trip-selector-modal.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import RedemptionIcon from '@/components/trips/redemptions/redemption-icon.vue';
import Icon from '@/components/ui/icon/icon.vue';
import EditRedemption from '@/components/trips/modals/edit-redemption.vue';
import CreateTripForRedemptionModal from '@/components/trips/modals/create-trip-for-redemption-modal.vue';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import { notify } from 'notiwind';
import { useTripsStore } from '@/store/trips';
import { Apii } from '@/services/api';
import Tooltip from '@/components/tooltip.vue';

const emit = defineEmits(['count-updated']);

const ApiService = new Apii();

const tripsStore = useTripsStore();
const { showRedemptionEditModal, attachAfterEditRedemption } =
    storeToRefs(tripsStore);

const editingFromUnfiled = ref(false);

const loading = ref(true);
const redemptions = ref([]);
const currentPage = ref(1);
const totalPages = ref(1);
const perPage = ref(20);
const totalResults = ref(0);

const showAttachModal = ref(false);
const selectedRedemption = ref(null);
const showCreateTripForRedemptionModal = ref(false);
const createTripRedemption = ref(null);

const fetchUnfiled = async (page = 1) => {
    loading.value = true;
    try {
        const response = await ApiService.apiCall(
            `/redemptions/unfiled?page=${page}`,
            'get'
        );
        const paginatedData = response.data || response;
        redemptions.value = paginatedData.data || [];
        currentPage.value = paginatedData.current_page || 1;
        totalPages.value = paginatedData.last_page || 1;
        totalResults.value = paginatedData.total || 0;
        emit('count-updated', totalResults.value);
    } catch (err) {
        console.error('Failed to fetch unfiled redemptions:', err);
        redemptions.value = [];
    } finally {
        loading.value = false;
    }
};

const handlePageChange = (page) => {
    fetchUnfiled(page);
};

const openAttachModal = (redemption) => {
    selectedRedemption.value = redemption;
    showAttachModal.value = true;
};

const closeAttachModal = () => {
    showAttachModal.value = false;
};

const handleAttachSuccess = (trip) => {
    closeAttachModal();
    fetchUnfiled(currentPage.value);
    tripsStore.getTrips();
};

const handleNewTripFromAttach = (redemption) => {
    closeAttachModal();
    openCreateTripForRedemptionModal(redemption);
};

const openCreateTripForRedemptionModal = (redemption) => {
    createTripRedemption.value = redemption;
    showCreateTripForRedemptionModal.value = true;
};

const handleTripCreatedWithRedemption = ({ trip, redemption }) => {
    showCreateTripForRedemptionModal.value = false;
    fetchUnfiled(currentPage.value);
    tripsStore.getTrips();
};

const openEditModal = async (redemption) => {
    editingFromUnfiled.value = true;
    await tripsStore.getRedemptionFormData();
    tripsStore.setActiveRedemption(redemption);
    tripsStore.setEditingUnfiledRedemption(true);
    tripsStore.setShowRedemptionEditModal(true);
};

// Delete confirmation
const showDeleteConfirm = ref(false);
const redemptionToDelete = ref(null);

const deleteConfirmMessage = computed(() => {
    if (!redemptionToDelete.value) return '';
    const title = getRedemptionTitle(redemptionToDelete.value);
    const date = redemptionToDelete.value.start_date
        ? formatDateShort(redemptionToDelete.value.start_date)
        : '';
    const endDate = redemptionToDelete.value.end_date
        ? formatDateShort(redemptionToDelete.value.end_date)
        : '';
    const dateRange =
        endDate && endDate !== date ? `${date} – ${endDate}` : date;
    const label = dateRange ? `'${title}, ${dateRange}'` : `'${title}'`;
    return `You are about to delete the redemption ${label}. This action cannot be undone.`;
});

const openDeleteConfirm = (redemption) => {
    redemptionToDelete.value = redemption;
    showDeleteConfirm.value = true;
};

const closeDeleteConfirm = () => {
    showDeleteConfirm.value = false;
};

const confirmDeleteRedemption = async () => {
    if (!redemptionToDelete.value) return;

    try {
        await ApiService.apiCall(
            `/redemptions/${redemptionToDelete.value.id}/ignore`,
            'post'
        );
        showDeleteConfirm.value = false;
        fetchUnfiled(currentPage.value);
        notify(
            {
                group: 'success',
                title: 'Redemption deleted',
                text: 'Redemption was successfully deleted.',
            },
            5000
        );
    } catch (err) {
        console.error('Failed to delete redemption:', err);
    }
};

const formatDateShort = (dateString) => {
    if (!dateString) return '';
    return dayjs(dateString).format('MMM D, YYYY');
};

const getRedemptionTitle = (redemption) => {
    const type = redemption.redemption_type?.slug;
    const typeName = redemption.redemption_type?.name || 'Redemption';

    if (type === 'flight' && redemption.travel_segments?.length > 0) {
        const segments = redemption.travel_segments;
        const origin = segments[0]?.origin_name || segments[0]?.origin || '';
        const destination =
            segments[segments.length - 1]?.destination_name ||
            segments[segments.length - 1]?.destination ||
            '';
        if (origin && destination) return `${origin} – ${destination}`;
        return typeName;
    }

    return redemption.name || typeName;
};

const getLayoverCount = (redemption) => {
    const type = redemption.redemption_type?.slug;
    if (type === 'flight' && redemption.travel_segments?.length > 1) {
        return redemption.travel_segments.length - 1;
    }
    return 0;
};

// Refresh unfiled list when edit modal closes
watch(showRedemptionEditModal, (newVal, oldVal) => {
    if (oldVal === true && newVal === false && editingFromUnfiled.value) {
        fetchUnfiled(currentPage.value);
        editingFromUnfiled.value = false;
    }
});

// Open attach modal after saving from Review modal with "Save & Add to trip"
watch(attachAfterEditRedemption, (redemption) => {
    if (redemption) {
        tripsStore.setAttachAfterEditRedemption(null);
        openAttachModal(redemption);
    }
});

const applyDeepLinkParams = async () => {
    const params = new URLSearchParams(window.location.search);
    const redemptionId = params.get('redemption');

    if (!redemptionId) {
        return;
    }

    const redemption = redemptions.value.find(
        (r) => String(r.id) === String(redemptionId)
    );

    if (redemption) {
        await openEditModal(redemption);
    }

    history.replaceState({}, document.title, window.location.pathname);
};

onMounted(async () => {
    const hasDeepLink = new URLSearchParams(window.location.search).has(
        'redemption'
    );

    if (hasDeepLink) {
        tripsStore.getRedemptionFormData();
    }

    await fetchUnfiled();
    applyDeepLinkParams();
});
</script>

<style scoped>
.trip-thumbnail {
    background-image: url('@@/images/default-placeholder.png');
    background-size: cover;
    background-position: center;
}
</style>
